import { Button, Heading, Input, Select, Spinner, Textarea } from '@vwfs-bronson/bronson-react';
import { Team } from '../../fes';
import {
  TeamFormState,
  TeamPanelMode,
  formatArray,
  formatMetadataValue,
  teamStatuses,
} from './teamForm';

export function TeamDetailPanel({
  error,
  form,
  isLoading,
  mode,
  onCancel,
  onEdit,
  onFormChange,
  onSave,
  team,
}: {
  error?: string;
  form: TeamFormState;
  isLoading: boolean;
  mode: TeamPanelMode;
  onCancel: () => void;
  onEdit: () => void;
  onFormChange: (field: keyof TeamFormState, value: string) => void;
  onSave: () => void;
  team: Team | null;
}) {
  if (!team && !isLoading) {
    return null;
  }

  const isEditing = mode === 'edit' || mode === 'saving';
  const isSaving = mode === 'saving';
  const canSave = form.name.trim().length > 0 && !isSaving;
  const editableFieldClassName = isEditing
    ? 'app-form-field app-form-field--editable'
    : 'app-form-field app-form-field--readonly';
  const editableWideFieldClassName = `${editableFieldClassName} app-form-field--wide`;

  return (
    <section className="app-team-panel" aria-labelledby="team-detail-heading">
      <div className="app-team-panel-header">
        <div>
          <div className="app-kicker">Team Detail</div>
          <Heading level={2} id="team-detail-heading" className="app-panel-title">
            {team?.name || 'Loading team'}
          </Heading>
        </div>
        <div className="app-actions">
          {!isEditing ? (
            <Button onClick={onEdit} disabled={!team || isLoading}>
              Edit
            </Button>
          ) : (
            <>
              <Button onClick={onSave} disabled={!canSave}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button secondary onClick={onCancel} disabled={isSaving}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="app-detail-loading" role="status">
          <Spinner center>Loading team...</Spinner>
        </div>
      ) : (
        <>
          {error ? (
            <div className="app-result app-team-error" role="alert">
              {error}
            </div>
          ) : null}
          <div className="app-detail-grid">
            <label className="app-form-field app-form-field--readonly" htmlFor="team-id">
              <span>Team ID</span>
              <Input id="team-id" value={team?.teamId || '-'} readOnly />
            </label>
            <label className={editableFieldClassName} htmlFor="team-name">
              <span>Name</span>
              <Input
                id="team-name"
                value={form.name}
                onChange={event => onFormChange('name', event.target.value)}
                readOnly={!isEditing}
              />
            </label>
            <label className={editableWideFieldClassName} htmlFor="team-description">
              <span>Description</span>
              <Textarea
                id="team-description"
                value={form.description}
                onChange={event => onFormChange('description', event.target.value)}
                readOnly={!isEditing}
                rows={3}
              />
            </label>
            <label className={editableFieldClassName} htmlFor="team-status">
              <span>Status</span>
              <Select
                id="team-status"
                value={form.status}
                onChange={event => onFormChange('status', event.target.value)}
                disabled={!isEditing}
              >
                {teamStatuses.map(status => (
                  <Select.Item key={status} value={status}>
                    {status}
                  </Select.Item>
                ))}
              </Select>
            </label>
            <label className={editableFieldClassName} htmlFor="team-installation-id">
              <span>Installation ID</span>
              <Input
                id="team-installation-id"
                value={!isEditing && !form.installationId ? '-' : form.installationId}
                onChange={event => onFormChange('installationId', event.target.value)}
                readOnly={!isEditing}
              />
            </label>
            <label className="app-form-field app-form-field--readonly app-form-field--wide" htmlFor="team-allowed-repos">
              <span>Allowed Repos</span>
              <Textarea id="team-allowed-repos" value={formatArray(team?.allowedRepos)} readOnly rows={3} />
            </label>
            <label className="app-form-field app-form-field--readonly" htmlFor="team-created-at">
              <span>Created At</span>
              <Input id="team-created-at" value={formatMetadataValue(team?.metadata?.createdAt)} readOnly />
            </label>
            <label className="app-form-field app-form-field--readonly" htmlFor="team-updated-at">
              <span>Updated At</span>
              <Input id="team-updated-at" value={formatMetadataValue(team?.metadata?.updatedAt)} readOnly />
            </label>
          </div>
        </>
      )}
    </section>
  );
}
